// Chemin: C:\gestion-copro\src\pages\vigie\AffaireDetailVigiePage.tsx
// Session 125 : Module "Mes dossiers" VIGIE — Page détail
// Session 126 : Fix getSignalements(coproprieteId) + enrichissement lien SENTINELLE avec description
// Session 128 : Ajout section Courriers liés (via getCourriersByAffaire) + rattachement courrier au dossier
// Session 129 : Chronologie courriers — rattacher/détacher via service avec traçabilité timeline
// Session 179 : Bouton "Marquer résolu / Rouvrir" — toggle resolu transversal + badge en-tête
// Session 235 : Suppression section Signalements du panneau droit + filtre chronologie (type signalement masqué)
// Session 262 : Fusion Documents + Courriers → section unique "Pièces du dossier"
//               Modal rattachement unifiée (docs + courriers non rattachés, filtrés, triés par date)
//               Suppression modal rattachement courrier séparée
// Session 262b : GED unifiée — collection `documents` uniquement (onglet Correspondances supprimé)
//                Suppression requête courriers — plus de doublon cross-collection
//                Filtre judicieux : catégories à vocation dossier uniquement
// Session 263 : Filtre chronologie type 'courrier' (relique ancienne collection courriers)
//               Tri intelligent modal rattachement — docs sémantiquement proches remontent en haut
// Principe ESCALATOR : sous-ensemble identique du CS (AffaireDetailPage.tsx)
// Différences VIGIE : pas d'AdminLayout, couleur ambre, routes /vigie/...,
//                     suppression autorisée (propriétaire = seul utilisateur),
//                     liens documents/signalements vers pages VIGIE

import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useCopropriete } from '../../context/CoproprieteContext';
import {
  Affaire,
  StatutAffaire,
  TYPE_AFFAIRE_LABELS,
  STATUT_AFFAIRE_LABELS,
  STATUT_AFFAIRE_COLORS,
  PRIORITE_AFFAIRE_LABELS,
  PRIORITE_AFFAIRE_COLORS,
  GARANTIE_LABELS,
  EvenementAffaire
} from '../../types/affaire.types';
import { Interlocuteur } from '../../types/interlocuteur.types';
import {
  getAffaireById,
  updateStatutAffaire,
  addEvenementToAffaire,
  rattacherDocument,
  detacherDocument,
  deleteAffaire
} from '../../services/affaire.service';
import { getInterlocuteur } from '../../services/interlocuteur.service';
import { collection, query, where, orderBy, getDocs, doc as firestoreDoc, updateDoc } from 'firebase/firestore';
import { db } from '../../services/firebase';
import {
  ArrowLeft,
  Loader2,
  AlertCircle,
  FolderOpen,
  Edit,
  Clock,
  FileText,
  Users,
  Trash2,
  Plus,
  X,
  Link as LinkIcon,
  Unlink,
  Sparkles,
  Mail,
  CheckCheck,
  Paperclip
} from 'lucide-react';

// ============================================
// TYPES LOCAUX
// ============================================

interface DocumentListItem {
  id: string;
  titre: string;
  dateDocument: any;
  fichierType: string;
  categorie?: string;
  sensCorrespondance?: string; // 'entrant' | 'sortant' — pour badge direction
}

// Session 262b : Item pour la modal de rattachement (GED uniquement)
interface PieceDisponible {
  id: string;
  titre: string;
  date: any;
  categorie?: string;
  sensCorrespondance?: string;
}

// Catégories GED exclues de la modal de rattachement (documents de référence ou modules dédiés)
const CATEGORIES_EXCLUES_MODAL = [
  'comptabilite', 'appel_fonds', 'releve_charges',
  'mon_bien', 'reglement_copropriete', 'contrat_syndic',
  'pv_ag', 'administratif',
];

// Session 263 : Mots vides exclus du calcul de pertinence
const MOTS_VIDES = new Set([
  'le', 'la', 'les', 'de', 'du', 'des', 'un', 'une', 'et', 'en', 'au', 'aux',
  'ce', 'se', 'sa', 'son', 'ses', 'mon', 'ma', 'mes', 'ton', 'ta', 'tes',
  'par', 'sur', 'sous', 'dans', 'avec', 'pour', 'que', 'qui', 'ou', 'à',
  'est', 'sont', 'était', 'ont', 'a', 'il', 'elle', 'ils', 'elles',
]);

/**
 * Session 263 : Calcule un score de pertinence entre le titre du dossier et le titre d'une pièce.
 * Retourne le nombre de mots significatifs communs (insensible à la casse).
 */
function scorerPertinence(titreDossier: string, titrePiece: string): number {
  const tokenize = (s: string): string[] =>
    s.toLowerCase()
      .replace(/[^a-zàâçéèêëîïôùûü\s]/g, ' ')
      .split(/\s+/)
      .filter(w => w.length >= 3 && !MOTS_VIDES.has(w));

  const motsDossier = new Set(tokenize(titreDossier));
  const motsPiece = tokenize(titrePiece);

  return motsPiece.filter(m => motsDossier.has(m)).length;
}

// ============================================
// COMPOSANT PRINCIPAL
// ============================================

export default function AffaireDetailVigiePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { userProfile, firebaseUser } = useAuth();
  const { coproprieteId } = useCopropriete();

  const [affaire, setAffaire] = useState<Affaire | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Données liées
  const [documentsLies, setDocumentsLies] = useState<DocumentListItem[]>([]);
  const [interlocuteursLies, setInterlocuteursLies] = useState<Interlocuteur[]>([]);

  // Session 262b : Pièces disponibles pour rattachement (GED uniquement, filtrées)
  const [piecesDisponibles, setPiecesDisponibles] = useState<PieceDisponible[]>([]);
  const [showRattachement, setShowRattachement] = useState(false);

  // Pour ajout note
  const [showAjoutNote, setShowAjoutNote] = useState(false);
  const [noteTexte, setNoteTexte] = useState('');

  // Pour changement statut
  const [showChangementStatut, setShowChangementStatut] = useState(false);
  const [nouveauStatut, setNouveauStatut] = useState<StatutAffaire>('en_attente');
  const [commentaireStatut, setCommentaireStatut] = useState('');

  // Pour suppression
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // ============================================
  // CHARGEMENT DES DONNÉES
  // ============================================

  useEffect(() => {
    if (!id || !coproprieteId) return;

    const loadData = async () => {
      try {
        setLoading(true);

        // Charger le dossier
        const affaireData = await getAffaireById(id);
        if (!affaireData) {
          setError('Dossier non trouvé');
          return;
        }
        setAffaire(affaireData);

        // Session 262b : GED uniquement — collection documents, triée par date décroissante
        const docsQuery = query(
          collection(db, 'documents'),
          where('coproprieteId', '==', coproprieteId),
          orderBy('createdAt', 'desc')
        );
        const docsSnapshot = await getDocs(docsQuery);
        const allDocs = docsSnapshot.docs.map(d => ({
          id: d.id,
          titre: d.data().titre || 'Sans titre',
          dateDocument: d.data().dateDocument || d.data().createdAt,
          fichierType: d.data().fichierType || '',
          categorie: d.data().categorie || 'non_classe',
          sensCorrespondance: d.data().sensCorrespondance || undefined,
        })) as DocumentListItem[];

        // Documents rattachés à ce dossier (via documentIds)
        const docsLies = allDocs.filter(d => affaireData.documentIds.includes(d.id));
        setDocumentsLies(docsLies);

        // Charger les interlocuteurs liés
        const interlocuteurs = await Promise.all(
          affaireData.interlocuteurIds.map(iId => getInterlocuteur(iId))
        );
        setInterlocuteursLies(interlocuteurs.filter(Boolean) as Interlocuteur[]);

        // Session 262b : Pièces disponibles — GED uniquement, non rattachées, catégories pertinentes
        const disponibles: PieceDisponible[] = allDocs
          .filter(d =>
            !affaireData.documentIds.includes(d.id) &&
            !CATEGORIES_EXCLUES_MODAL.includes(d.categorie || '')
          )
          .map(d => ({
            id: d.id,
            titre: d.titre,
            date: d.dateDocument,
            categorie: d.categorie,
            sensCorrespondance: d.sensCorrespondance,
          }));
        setPiecesDisponibles(disponibles);

      } catch (err) {
        console.error('Erreur chargement:', err);
        setError('Erreur lors du chargement');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [id, coproprieteId]);

  // ============================================
  // Session 263 : Tri intelligent des pièces disponibles
  // Les pièces dont le titre partage des mots avec le dossier remontent en haut.
  // ============================================

  const piecesTriees = useMemo(() => {
    if (!affaire || piecesDisponibles.length === 0) return { pertinentes: [], autres: [] };

    const avec_score = piecesDisponibles.map(p => ({
      ...p,
      score: scorerPertinence(affaire.titre, p.titre),
    }));

    const pertinentes = avec_score
      .filter(p => p.score > 0)
      .sort((a, b) => b.score - a.score);

    const autres = avec_score
      .filter(p => p.score === 0)
      .sort((a, b) => {
        const da = a.date?.toDate ? a.date.toDate() : new Date(a.date || 0);
        const db2 = b.date?.toDate ? b.date.toDate() : new Date(b.date || 0);
        return db2.getTime() - da.getTime();
      });

    return { pertinentes, autres };
  }, [affaire, piecesDisponibles]);

  // ============================================
  // FORMATAGE
  // ============================================

  const formatDate = (timestamp: any) => {
    if (!timestamp) return 'Non renseigné';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    if (isNaN(date.getTime())) return 'Non renseigné';
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  const formatDateShort = (timestamp: any) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    if (isNaN(date.getTime())) return '';
    return date.toLocaleDateString('fr-FR');
  };

  const getStatutLabelVigie = (statut: StatutAffaire): string => {
    if (statut === 'en_cours') return 'Ouvert';
    if (statut === 'cloturee') return 'Résolu';
    return STATUT_AFFAIRE_LABELS[statut];
  };

  const getEvenementIcon = (type: EvenementAffaire['type']) => {
    switch (type) {
      case 'creation': return <FolderOpen className="w-4 h-4" />;
      case 'document': return <FileText className="w-4 h-4" />;
      case 'courrier': return <Mail className="w-4 h-4" />;
      case 'decision': return <AlertCircle className="w-4 h-4" />;
      case 'note': return <Edit className="w-4 h-4" />;
      case 'cloture': return <X className="w-4 h-4" />;
      default: return <Clock className="w-4 h-4" />;
    }
  };

  // ============================================
  // HANDLERS
  // ============================================

  const handleChangementStatut = async () => {
    if (!affaire || !firebaseUser) return;
    try {
      const userNom = `${userProfile?.prenom || ''} ${userProfile?.nom || ''}`.trim() || 'Utilisateur';
      await updateStatutAffaire(affaire.id, nouveauStatut, firebaseUser.uid, userNom, commentaireStatut);
      const updated = await getAffaireById(affaire.id);
      if (updated) setAffaire(updated);
      setShowChangementStatut(false);
      setCommentaireStatut('');
    } catch (err) {
      console.error('Erreur changement statut:', err);
      setError('Erreur lors du changement de statut');
    }
  };

  const handleAjoutNote = async () => {
    if (!affaire || !firebaseUser || !noteTexte.trim()) return;
    try {
      const userNom = `${userProfile?.prenom || ''} ${userProfile?.nom || ''}`.trim() || 'Utilisateur';
      await addEvenementToAffaire(affaire.id, {
        type: 'note',
        titre: 'Note ajoutée',
        description: noteTexte.trim(),
        auteurId: firebaseUser.uid,
        auteurNom: userNom
      });
      const updated = await getAffaireById(affaire.id);
      if (updated) setAffaire(updated);
      setShowAjoutNote(false);
      setNoteTexte('');
    } catch (err) {
      console.error('Erreur ajout note:', err);
      setError("Erreur lors de l'ajout de la note");
    }
  };

  const handleDetacherDocument = async (documentId: string) => {
    if (!affaire) return;
    if (!window.confirm('Détacher ce document du dossier ?')) return;
    try {
      await detacherDocument(affaire.id, documentId);
      window.location.reload();
    } catch (err) {
      console.error('Erreur détachement:', err);
      setError('Erreur lors du détachement');
    }
  };

  // Session 262b : Rattachement depuis la modal — GED uniquement
  const handleRattacherPiece = async (piece: PieceDisponible) => {
    if (!affaire || !firebaseUser) return;
    try {
      const userNom = `${userProfile?.prenom || ''} ${userProfile?.nom || ''}`.trim() || 'Utilisateur';
      await rattacherDocument(affaire.id, piece.id, piece.titre, firebaseUser.uid, userNom);
      window.location.reload();
    } catch (err) {
      console.error('Erreur rattachement:', err);
      setError('Erreur lors du rattachement');
    }
  };

  const handleSupprimer = async () => {
    if (!affaire) return;
    setDeleting(true);
    try {
      await deleteAffaire(affaire.id);
      navigate('/vigie/dossiers');
    } catch (err) {
      console.error('Erreur suppression:', err);
      setError('Erreur lors de la suppression');
      setDeleting(false);
      setShowDeleteModal(false);
    }
  };

  const [actionEnCours, setActionEnCours] = useState<string | null>(null);

  const handleToggleResolu = async () => {
    if (!affaire) return;
    setActionEnCours('resolu');
    try {
      const newResolu = !affaire.resolu;
      await updateDoc(firestoreDoc(db, 'affaires', affaire.id), { resolu: newResolu });
      setAffaire({ ...affaire, resolu: newResolu });
    } catch (err) {
      console.error('Erreur toggle résolu:', err);
      setError('Impossible de modifier le statut résolu');
    } finally {
      setActionEnCours(null);
    }
  };

  // ============================================
  // RENDU — ITEM PIÈCE (réutilisé dans la modal)
  // ============================================

  const renderPieceModalItem = (piece: PieceDisponible) => (
    <li
      key={`dispo-${piece.id}`}
      className="flex justify-between items-center p-3 border border-gray-100 rounded-lg hover:bg-gray-50 gap-3"
    >
      <div className="flex-1 min-w-0">
        {piece.sensCorrespondance && (
          <div className="mb-0.5">
            {piece.sensCorrespondance === 'sortant' ? (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-xs rounded bg-green-50 text-green-700 font-medium">
                <Mail className="w-3 h-3" />
                Envoyé
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-xs rounded bg-blue-50 text-blue-700 font-medium">
                <Mail className="w-3 h-3" />
                Reçu
              </span>
            )}
          </div>
        )}
        <div className="font-medium text-sm text-gray-900 truncate">{piece.titre}</div>
        <div className="text-xs text-gray-500">{formatDateShort(piece.date)}</div>
      </div>
      <button
        onClick={() => handleRattacherPiece(piece)}
        className="text-amber-600 hover:text-amber-800 text-sm font-medium flex-shrink-0"
      >
        Rattacher
      </button>
    </li>
  );

  // ============================================
  // RENDU PRINCIPAL
  // ============================================

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
      </div>
    );
  }

  if (error || !affaire) {
    return (
      <div className="space-y-4">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error || 'Dossier non trouvé'}</p>
        </div>
        <Link to="/vigie/dossiers" className="text-amber-600 hover:underline inline-flex items-center gap-1">
          <ArrowLeft className="w-4 h-4" />
          Retour aux dossiers
        </Link>
      </div>
    );
  }

  // Session 262b : Pièces rattachées = documents GED uniquement, triés par date décroissante
  const piecesRattachees = [...documentsLies].sort((a, b) => {
    const da = a.dateDocument?.toDate ? a.dateDocument.toDate() : new Date(a.dateDocument || 0);
    const db2 = b.dateDocument?.toDate ? b.dateDocument.toDate() : new Date(b.dateDocument || 0);
    return db2.getTime() - da.getTime();
  });

  return (
    <div className="space-y-6">

      {/* En-tête */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4">
        <div>
          <button
            onClick={() => navigate('/vigie/dossiers')}
            className="text-gray-600 hover:text-gray-900 mb-2 inline-flex items-center gap-1 text-sm"
          >
            <ArrowLeft className="w-4 h-4" />
            Retour aux dossiers
          </button>
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-2xl font-bold text-gray-900">{affaire.titre}</h1>
            <span className={`px-2 py-1 text-xs font-semibold rounded-full ${STATUT_AFFAIRE_COLORS[affaire.statut]}`}>
              {getStatutLabelVigie(affaire.statut)}
            </span>
            {affaire.resolu && (
              <span className="px-2 py-1 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-700 flex items-center gap-1">
                <CheckCheck className="w-3 h-3" />
                Résolu
              </span>
            )}
          </div>
          <p className="text-gray-500 mt-1">
            {affaire.numero} | {TYPE_AFFAIRE_LABELS[affaire.type]}
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Link
            to={`/vigie/dossiers/${affaire.id}/modifier`}
            className="inline-flex items-center gap-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-sm"
          >
            <Edit className="w-4 h-4" />
            Modifier
          </Link>
          <button
            onClick={() => setShowChangementStatut(true)}
            className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors text-sm"
          >
            Changer statut
          </button>
          <button
            onClick={handleToggleResolu}
            disabled={actionEnCours === 'resolu'}
            className={`inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg transition-colors disabled:opacity-50 ${
              affaire.resolu
                ? 'bg-emerald-600 text-white hover:bg-emerald-700'
                : 'border border-emerald-300 text-emerald-700 hover:bg-emerald-50'
            }`}
            title={affaire.resolu ? 'Rouvrir ce dossier' : 'Marquer ce dossier comme résolu'}
          >
            {actionEnCours === 'resolu' ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <CheckCheck className="w-4 h-4" />
            )}
            {affaire.resolu ? 'Résolu' : 'Marquer résolu'}
          </button>
          <button
            onClick={() => setShowDeleteModal(true)}
            className="inline-flex items-center gap-1 px-3 py-2 border border-red-200 rounded-lg text-red-600 hover:bg-red-50 transition-colors text-sm"
            title="Supprimer ce dossier"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Grille principale */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

        {/* Colonne gauche — Infos et Timeline */}
        <div className="lg:col-span-2 space-y-6">

          {/* Informations */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold mb-4">Informations</h2>
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <span className="text-sm text-gray-500">Priorité</span>
                <div className={`inline-flex ml-2 px-2 py-1 text-xs font-semibold rounded-full ${PRIORITE_AFFAIRE_COLORS[affaire.priorite]}`}>
                  {PRIORITE_AFFAIRE_LABELS[affaire.priorite]}
                </div>
              </div>
              <div>
                <span className="text-sm text-gray-500">Ouvert le</span>
                <div className="text-gray-900">{formatDateShort(affaire.dateOuverture)}</div>
              </div>
              {affaire.garantieApplicable && (
                <div className="col-span-2">
                  <span className="text-sm text-gray-500">Garantie applicable</span>
                  <div className="text-gray-900">{GARANTIE_LABELS[affaire.garantieApplicable]}</div>
                </div>
              )}
              {affaire.montantEstime && (
                <div>
                  <span className="text-sm text-gray-500">Montant estimé</span>
                  <div className="text-gray-900">{affaire.montantEstime.toLocaleString('fr-FR')} EUR</div>
                </div>
              )}
            </div>
            <div>
              <span className="text-sm text-gray-500">Description</span>
              <p className="text-gray-900 mt-1 whitespace-pre-wrap">{affaire.description}</p>
            </div>
          </div>

          {/* Timeline / Chronologie */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold">Chronologie</h2>
              <button
                onClick={() => setShowAjoutNote(true)}
                className="text-sm text-amber-600 hover:text-amber-800 inline-flex items-center gap-1"
              >
                <Plus className="w-4 h-4" />
                Ajouter une note
              </button>
            </div>

            <div className="space-y-4">
              {affaire.evenements
                // Session 235 : masquer signalements
                // Session 263 : masquer 'courrier' — relique de l'ancienne collection courriers,
                //               remplacé par 'document' dans la GED unifiée
                .filter((evenement) =>
                  evenement.type !== 'signalement' &&
                  evenement.type !== 'courrier'
                )
                .sort((a, b) => b.date.toMillis() - a.date.toMillis())
                .map((evenement) => (
                  <div key={evenement.id} className="flex gap-4">
                    <div className="flex-shrink-0 w-8 h-8 bg-amber-50 rounded-full flex items-center justify-center text-amber-600">
                      {getEvenementIcon(evenement.type)}
                    </div>
                    <div className="flex-1">
                      <div className="flex justify-between items-start">
                        <div className="font-medium text-gray-900">{evenement.titre}</div>
                        <div className="text-sm text-gray-500">{formatDate(evenement.date)}</div>
                      </div>
                      {evenement.description && (
                        <p className="text-gray-600 text-sm mt-1">{evenement.description}</p>
                      )}
                      {evenement.auteurNom && (
                        <p className="text-gray-400 text-xs mt-1">Par {evenement.auteurNom}</p>
                      )}
                    </div>
                  </div>
                ))}
            </div>

            {affaire.evenements.length === 0 && (
              <p className="text-gray-500 text-sm text-center py-4">Aucun événement dans la chronologie</p>
            )}
          </div>
        </div>

        {/* Colonne droite — Éléments liés */}
        <div className="space-y-6">

          {/* Bouton SENTINELLE */}
          <Link
            to={`/vigie/sentinelle?contexte=dossier&id=${affaire.id}&titre=${encodeURIComponent(affaire.titre)}&description=${encodeURIComponent(affaire.description || '')}&nbDocs=${affaire.documentIds.length}`}
            className="flex items-center justify-center gap-2 w-full px-4 py-3 bg-gradient-to-r from-amber-500 to-amber-600 text-white rounded-xl hover:from-amber-600 hover:to-amber-700 transition-colors shadow-sm"
          >
            <Sparkles className="w-5 h-5" />
            Demander à SENTINELLE
          </Link>

          {/* Contacts */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Users className="w-5 h-5 text-gray-400" />
              Contacts
            </h2>
            {interlocuteursLies.length === 0 ? (
              <p className="text-gray-500 text-sm">Aucun contact lié</p>
            ) : (
              <ul className="space-y-2">
                {interlocuteursLies.map((interlocuteur) => (
                  <li key={interlocuteur.id} className="text-sm">
                    <Link
                      to={`/vigie/contacts`}
                      className="text-amber-600 hover:underline"
                    >
                      {interlocuteur.raisonSociale}
                    </Link>
                    <span className="text-gray-500 ml-2">({interlocuteur.type})</span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Session 262b : Section "Pièces du dossier" — GED uniquement */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold flex items-center gap-2">
                <Paperclip className="w-5 h-5 text-gray-400" />
                Pièces du dossier ({piecesRattachees.length})
              </h2>
              <button
                onClick={() => setShowRattachement(true)}
                className="text-sm text-amber-600 hover:text-amber-800 inline-flex items-center gap-1"
              >
                <LinkIcon className="w-4 h-4" />
                Rattacher
              </button>
            </div>

            {piecesRattachees.length === 0 ? (
              <p className="text-gray-500 text-sm">Aucune pièce rattachée</p>
            ) : (
              <ul className="space-y-3">
                {piecesRattachees.map((doc) => (
                  <li key={doc.id} className="flex justify-between items-start border-b border-gray-100 pb-2 last:border-0 last:pb-0">
                    <div className="flex-1 min-w-0 pr-2">
                      {/* Badge direction si correspondance */}
                      {doc.sensCorrespondance && (
                        <div className="mb-0.5">
                          {doc.sensCorrespondance === 'sortant' ? (
                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-xs rounded bg-green-50 text-green-700 font-medium">
                              <Mail className="w-3 h-3" />
                              Envoyé
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-xs rounded bg-blue-50 text-blue-700 font-medium">
                              <Mail className="w-3 h-3" />
                              Reçu
                            </span>
                          )}
                        </div>
                      )}
                      <Link
                        to={`/vigie/documents/${doc.id}`}
                        className="text-amber-600 hover:underline text-sm font-medium block truncate"
                      >
                        {doc.titre}
                      </Link>
                      <div className="text-xs text-gray-500 mt-0.5">{formatDateShort(doc.dateDocument)}</div>
                    </div>
                    <button
                      onClick={() => handleDetacherDocument(doc.id)}
                      className="text-red-400 hover:text-red-600 flex-shrink-0 mt-1"
                      title="Détacher"
                    >
                      <Unlink className="w-4 h-4" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Actions */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold mb-4">Actions</h2>
            <button
              onClick={() => setShowDeleteModal(true)}
              className="w-full flex items-center justify-center gap-2 px-4 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 transition-colors"
            >
              <Trash2 className="w-4 h-4" />
              Supprimer le dossier
            </button>
          </div>
        </div>
      </div>

      {/* ============================================ */}
      {/* MODALS                                       */}
      {/* ============================================ */}

      {/* Modal Changement Statut */}
      {showChangementStatut && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6 space-y-4">
            <h3 className="text-lg font-semibold">Changer le statut</h3>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nouveau statut</label>
              <select
                value={nouveauStatut}
                onChange={(e) => setNouveauStatut(e.target.value as StatutAffaire)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
              >
                <option value="ouverte">Ouvert</option>
                <option value="en_attente">En attente</option>
                <option value="cloturee">Résolu</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Commentaire (optionnel)</label>
              <textarea
                value={commentaireStatut}
                onChange={(e) => setCommentaireStatut(e.target.value)}
                rows={3}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                placeholder="Raison du changement..."
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button
                onClick={() => setShowChangementStatut(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Annuler
              </button>
              <button
                onClick={handleChangementStatut}
                className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700"
              >
                Valider
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Ajout Note */}
      {showAjoutNote && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6 space-y-4">
            <h3 className="text-lg font-semibold">Ajouter une note</h3>
            <textarea
              value={noteTexte}
              onChange={(e) => setNoteTexte(e.target.value)}
              rows={4}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
              placeholder="Votre note..."
            />
            <div className="flex justify-end gap-2">
              <button
                onClick={() => { setShowAjoutNote(false); setNoteTexte(''); }}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Annuler
              </button>
              <button
                onClick={handleAjoutNote}
                disabled={!noteTexte.trim()}
                className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50"
              >
                Ajouter
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Session 262b : Modal Rattachement — GED uniquement, filtre catégories pertinentes */}
      {/* Session 263 : Tri intelligent — pertinentes en haut, autres en dessous avec séparateur */}
      {showRattachement && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full p-6 max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between mb-4 flex-shrink-0">
              <h3 className="text-lg font-semibold">Rattacher une pièce</h3>
              <button
                onClick={() => setShowRattachement(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {piecesDisponibles.length === 0 ? (
              <p className="text-gray-500 text-sm py-4">Aucune pièce disponible à rattacher.</p>
            ) : (
              <ul className="space-y-2 overflow-y-auto flex-1">

                {/* Pièces pertinentes (score > 0) */}
                {piecesTriees.pertinentes.length > 0 && (
                  <>
                    <li className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2">
                      <span className="text-sm font-bold text-amber-800 uppercase tracking-wide">
                        En lien avec ce dossier
                      </span>
                    </li>
                    {piecesTriees.pertinentes.map(renderPieceModalItem)}
                  </>
                )}

                {/* Séparateur si les deux groupes sont présents */}
                {piecesTriees.pertinentes.length > 0 && piecesTriees.autres.length > 0 && (
                  <li className="rounded-lg bg-gray-100 border border-gray-200 px-3 py-2 mt-2">
                    <span className="text-sm font-bold text-gray-500 uppercase tracking-wide">
                      Autres documents
                    </span>
                  </li>
                )}

                {/* Pièces sans lien apparent */}
                {piecesTriees.autres.map(renderPieceModalItem)}

              </ul>
            )}

            <div className="flex justify-end mt-4 flex-shrink-0">
              <button
                onClick={() => setShowRattachement(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Suppression */}
      {showDeleteModal && affaire && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 rounded-full bg-red-100">
              <Trash2 className="w-6 h-6 text-red-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 text-center mb-2">
              Confirmer la suppression
            </h3>
            <p className="text-sm font-medium text-gray-900 text-center mb-2 px-4 py-2 bg-gray-100 rounded">
              {affaire.titre}
            </p>
            <p className="text-sm text-gray-500 text-center mb-2">
              Les signalements et documents rattachés ne seront pas supprimés.
            </p>
            <p className="text-sm text-red-600 text-center mb-6">
              Cette action est irréversible.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                disabled={deleting}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Annuler
              </button>
              <button
                onClick={handleSupprimer}
                disabled={deleting}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Supprimer'}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
